import { useState, useRef, useEffect, useCallback } from 'react';
import cn from 'classnames';

import OPE from 'js/on-page-editing';

import Accordion from 'components/accordion/Accordion';
import IconWarning from 'components/icon-warning/IconWarning';
import ContentArea from 'components/content-area/ContentArea';
import Icon from 'components/icon/Icon';

import { IAccordionWithContentArea } from 'source/NFR.types';
export interface AccordionWithContentAreaProps extends IAccordionWithContentArea {
  addAnchorHash?: (hash: string) => void;
  removeAnchorHash?: (hash: string) => void;
}

const AccordionWithContentArea: React.FC<AccordionWithContentAreaProps> = ({
  content,
  accordion,
  onPageEditing = {},
  title,
  iconWarning,
  share,
  htmlId,
  initiallyOpen,
  addAnchorHash = () => {},
  removeAnchorHash = () => {}
}) => {
  const [hasOpened, setHasOpened] = useState<boolean | undefined>();
  const isEditMode = Object.keys(onPageEditing).length > 0;
  const contentRef = useRef<HTMLDivElement>(null);

  const disableTabbing = (): void => {
    if (!contentRef.current) return;

    const interactiveElements = [
      ...contentRef.current.getElementsByTagName('a'),
      ...contentRef.current.getElementsByTagName('button')
    ];
    interactiveElements.forEach(link => {
      link.setAttribute('tabindex', '-1');
    });
  };

  const enableTabbing = (): void => {
    if (!contentRef.current) return;

    const interactiveElements = [
      ...contentRef.current.getElementsByTagName('a'),
      ...contentRef.current.getElementsByTagName('button')
    ];
    interactiveElements.forEach(link => {
      link.setAttribute('tabindex', '0');
    });
  };

  // Pure function to determine if accordion should expand - no side effects
  const shouldExpandOnMount = useCallback((): boolean => {
    const isTargetedByAnchor = htmlId && `#${htmlId}` === window.location.hash;
    const noAnchorAndOpen = !window.location.hash && initiallyOpen;
    return isTargetedByAnchor || noAnchorAndOpen;
  }, [htmlId, initiallyOpen]);

  useEffect(() => {
    const shouldOpen = shouldExpandOnMount();
    if (shouldOpen) {
      // Handle side effects for initially open accordion
      if (htmlId) addAnchorHash(`#${htmlId}`);
      setHasOpened(true);
      enableTabbing();

      // Manually scroll if anchor-linked as expanding the accordion disrupts browsers anchor-link-scrolling.
      const isTargetedByAnchor = htmlId && `#${htmlId}` === window.location.hash;
      if (isTargetedByAnchor) {
        setTimeout(() => {
          const element = document.getElementById(htmlId);
          if (element) {
            window.scrollTo(0, element.offsetTop);
          }
        }, 500);
      }
    } else {
      disableTabbing();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return content ? (
    <Accordion {...accordion} expandOnMount={shouldExpandOnMount} showButtonText={false} buttonTextForLabel={title}>
      {({ Button, Collapse, isOpen, toggle }) => {
        const onClick = (): void => {
          toggle();
          const isOpening = !isOpen;
          if (isOpening) {
            if (htmlId) addAnchorHash(`#${htmlId}`);
            enableTabbing();
            setTimeout(() => {
              setHasOpened(true);
            }, 300);
          } else {
            if (htmlId) removeAnchorHash(`#${htmlId}`);
            disableTabbing();
            setHasOpened(false);
          }
        };
        return (
          <div className="accordion-with-content-area--anchor-target" id={htmlId}>
            <div className="accordion-with-content-area">
              <Button
                className={cn('accordion-with-content-area--header', {
                  'accordion-with-content-area__header--is-open': isOpen
                })}
                onClick={isEditMode ? () => {} : onClick}
              >
                {title && (
                  <div className="accordion-with-content-area__title-container">
                    {iconWarning && (
                      <IconWarning className="accordion-with-content-area__title-icon" {...iconWarning} />
                    )}
                    <h3 className="accordion-with-content-area__title" {...OPE(onPageEditing.title)}>
                      {title}
                    </h3>
                  </div>
                )}

                <Icon className="accordion-with-content-area--icon" name="small-arrow-down" fill={true} />
              </Button>

              <Collapse
                className={cn('accordion-with-content-area--collapse', {
                  'accordion-with-content-area__collapse--closed': !isOpen,
                  'accordion-with-content-area__collapse--opened': hasOpened
                })}
                closedHeight={80}
              >
                <div className="accordion-with-content-area--content" ref={contentRef}>
                  <ContentArea
                    additionalComponentProps={{
                      TextWithSidebar: {
                        headingLevel: 4,
                        isProposalPage: true,
                        share: share,
                        isOpen: !isOpen ? false : isOpen
                      }
                    }}
                    className="accordion-with-content-area--content-area"
                    {...content}
                  />
                </div>
              </Collapse>
            </div>
          </div>
        );
      }}
    </Accordion>
  ) : null;
};

AccordionWithContentArea.displayName = 'AccordionWithContentArea';

export default AccordionWithContentArea;
